import { useMemo } from 'react';
import { lessonsEditorApi, type CourseForEditorList } from 'Lessons';
import { type EditorCourse } from './StreamEditor.types';
import { titleWithTag } from 'ContentItemEditor';

/*
    Every course an editor can point at, in one request. Four pickers on this page need the same
    list, so one cached query serves all four.
*/
// The wire shape flattened for a picker. The cohort editor reads its own query but the same shape.
export function toEditorCourse(course: CourseForEditorList): EditorCourse {
    return {
        id: course.id,
        title: course.title,
        tag: course.tag,
        locale: course.locale,
        localePackId: course.localePack?.id ?? null,
        wasPublished: course.wasPublished,
        requirementIdentifier: course.localePack?.requirementIdentifier ?? null,
        groupNames: (course.localePack?.groups || []).map(group => group.name).sort(),
        localePack: course.localePack ? { contentItems: course.localePack.contentItems } : null,
    };
}

export function useEditorCourses() {
    const { data, isLoading } = lessonsEditorApi.useGetCoursesForEditorListQuery();

    const courses = useMemo<EditorCourse[]>(() => (data?.contents?.lessonStreams || []).map(toEditorCourse), [data]);

    const coursesById = useMemo(() => Object.fromEntries(courses.map(course => [course.id, course])), [courses]);

    return { courses, coursesById, isLoading };
}

export function courseTitleWithTag(course: { title: string; tag?: string | null } | undefined) {
    // What the Angular list showed for a course id it could not resolve. A course can be deleted
    // while another still recommends it.
    if (!course) return '☢☢☢ STREAM NOT FOUND ☢☢☢ Probably deleted?';
    return titleWithTag(course);
}
